import {
  useCode,
  type CodeSource,
  type TargetLanguage,
} from '@/services/CodeService';
import { Palette } from './Palette';
import { Loader } from './Loader';
import {
  Column,
  Font,
  NavigationFilter,
  PrimaryActionColor,
  SecondaryButton,
  ThinBorderStyle,
} from './DesignSystem';
import { useCallback, useEffect, useState } from 'react';

export function CodePalette() {
  const { state, service } = useCode();
  const [targetLanguage, setTargetLanguage] = useState<TargetLanguage>('css');

  const generateCode = useCallback(
    ({
      source,
      targetLanguage,
    }: {
      source: CodeSource;
      targetLanguage: TargetLanguage;
    }) => {
      if (targetLanguage === undefined) {
        return;
      }

      return service.generateCode({ source, targetLanguage });
    },
    [service],
  );

  const { source, type } = state;
  useEffect(() => {
    if (
      state.type === 'displaying-code' &&
      state.targetLanguage === targetLanguage
    ) {
      return;
    }

    if (state.errorMessage !== null) {
      return;
    }

    if (source === undefined) {
      return;
    }

    generateCode({ source, targetLanguage });
  }, [generateCode, source, targetLanguage, type, state]);

  if (source === undefined) {
    return <></>;
  }

  // TODO(LAB-316): I am kind of proud of myself, in a "this is the worst code
  // I have ever written ever" kind of way. But oh god, this needs to be fixed.
  // Long term, the generate_code endpoint should return a tree structure that
  // can be manipulated for syntax highlighting directly.
  function processCode({ code }: { code: string }): string {
    function keywordSyntaxHighlight({
      code,
      keywords,
    }: {
      code: string;
      keywords: string[];
    }): string {
      let highlightedCode = code;
      keywords.forEach((keyword) => {
        highlightedCode = highlightedCode.replaceAll(
          keyword,
          `<span style='color: ${PrimaryActionColor}'>${keyword}</span>`,
        );
      });
      return highlightedCode;
    }

    const formattedCode = code
      .replaceAll('case', '&nbsp&nbspcase')
      .replaceAll('func', '\n&nbsp&nbspfunc')
      .replaceAll('switch', '&nbsp&nbsp&nbsp&nbspswitch')
      .replaceAll('return', '&nbsp&nbsp&nbsp&nbsp&nbsp&nbsp&nbsp&nbspreturn')
      .replaceAll('case .', '&nbsp&nbsp&nbsp&nbspcase .')
      .replaceAll('}\n  }', '&nbsp&nbsp&nbsp&nbsp}\n&nbsp&nbsp}')
      .replaceAll('\n', '<br/>');

    return keywordSyntaxHighlight({
      code: formattedCode,
      keywords: ['func', 'return', 'case', 'switch', 'enum'],
    });
  }

  const showCode =
    !state.isLoading &&
    state.errorMessage === null &&
    type === 'displaying-code';
  const showError = !state.isLoading && state.errorMessage !== null;

  return (
    <Palette
      title="code"
      windowID="code"
      trailingAction={
        <NavigationFilter
          options={['css', 'swift']}
          onSelection={(option) => setTargetLanguage(option)}
        />
      }
    >
      <Column style={{ width: '100%' }}>
        {state.isLoading && <Loader height={{ type: 'fill' }} />}
        {showCode && (
          <div
            style={{ ...ThinBorderStyle, paddingInline: 8, marginInline: 8 }}
          >
            <Font kind="font-source-code">
              <p
                dangerouslySetInnerHTML={{
                  __html: processCode({ code: state.code }),
                }}
              />
            </Font>
          </div>
        )}
        {showError && (
          <Column
            role="alert"
            style={{
              ...ThinBorderStyle,
              paddingInline: 8,
              marginInline: 8,
              paddingBlock: 8,
              alignItems: 'center',
            }}
          >
            <Font
              kind="font-body"
              style={{ fontSize: 12, lineHeight: 1.4, textAlign: 'center' }}
            >
              {state.errorMessage}
            </Font>
            <SecondaryButton
              text="Retry"
              onClick={() => {
                generateCode({ source, targetLanguage });
              }}
            />
          </Column>
        )}
      </Column>
    </Palette>
  );
}
